import React, { useEffect, useRef, useState, useContext } from 'react';
import { IVehicleSearch } from '../../interfaces/VehicleSearch';
import { isUnderTwentyOne, priceFormat } from "../MakeReservation/util";
import { translateService } from "../../services/TranslateService";
import {baseOnSalesPrice, basePrice, discountValue as calculateDiscountValue} from "../MakeReservation/pricingCalculations";
import { DataContext } from "../../contexts/dataContext";
import { Backdrop } from "@mui/material";
import CalendarComponent from "../VehicleSearch/reservedTable";
import axios from "axios";
import moment from "moment";
import { InfoWindowSingleton } from './InfoWindowSingleton';
import {VipCodeFromMap} from "../VehicleSearch/VipCodeVehicles";
import Modal from "react-modal";
import {cStylesMaps} from "../../CustomModalStyles";
import { buildVehicleItem } from "../../services/ga4Ecommerce";
import '../../App.scss';

// Marker zIndex value set with following priority:
// me marker = 100
// available vehicles = 70
// available clusters = 50
// not available vehicles = 20;
// not available clusters = 10;

interface googleMapProps {
    lat: number,
    lng: number,
    width: string,
}

interface IiconMapping {
    [key: string]: string;
}

declare var google: any;
declare global {
    interface Window {
        google: any;
    }
}
interface IMarker {
    vehicleId: number;
    inCluster: boolean;
    marker: google.maps.marker.AdvancedMarkerElement;
}

interface handleMarkerClickProps {
    marker: google.maps.marker.AdvancedMarkerElement,
    vehicle: IVehicleSearch,
}
const fontFamily = "'Oswald', 'Nunito', Roboto, -apple-system, BlinkMacSystemFont, " +
    "'Segoe UI', 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';"

const iconMapping: IiconMapping = {
    compact: 'car',
    stw: 'car-large',
    suv: 'car-large',
    minibus: 'minibus',
    van: 'van',
    default: 'car',
    vip: 'vip'
};

const GoogleMap = ({ lat, lng, width }: googleMapProps) => {
    const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;
    const mapRef = useRef(null);
    const dataContext = useContext(DataContext);
    const [, _setMarkers] = useState<IMarker[]>([]);
    const [offsetMarkers, _setOffsetMarkers] = useState<IMarker[]>([]);
    const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null);
    const [selectedMarker, setSelectedMarker] = useState<google.maps.LatLngLiteral| undefined>(undefined)
    const [greenMarker, setGreenMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);
    const [originToVehicleLine, setOriginToVehicleLine] = useState<google.maps.Polyline | null>(null)
    const [reservationTable, setReservationTable] = useState<boolean>(false);
    const [reservedSlots, setReservedSlots] = React.useState<any>(undefined);
    const [reservedSlotsMessages, setReservedSlotsMessages] = React.useState<any>(undefined);
    const [, _setDrawLine] = useState<google.maps.Polyline[]>([]);
    const [selectedVipCar, setSelectedVipCar] = useState<IVehicleSearch | undefined>();
    const [vipCodeCheckOpen, setVipCodeCheckOpen] = useState<boolean>(false);
    const [markersLoading, setMarkersLoading] = useState<boolean>(false)
    const [mapMarkerData, setMapMarkerData] = useState<IVehicleSearch[]>([]);
    const [oneWayEnabled, setOneWayEnabled] = useState<boolean>(false);
    const [onewayReturnMarker, setOnewayReturnMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);
    const [showGoogleMap, setShowGoogleMap] = useState(false);

    // Refs to track current markers for imperative cleanup (avoids stale closures in React 19)
    const markersRef = useRef<IMarker[]>([]);
    const offsetMarkersRef = useRef<IMarker[]>([]);
    const drawLineRef = useRef<google.maps.Polyline[]>([]);

    const setMarkers = (newMarkers: IMarker[]) => {
        markersRef.current = newMarkers;
        _setMarkers(newMarkers);
    };
    const setOffsetMarkers = (newMarkers: IMarker[] | ((prev: IMarker[]) => IMarker[])) => {
        if (typeof newMarkers === 'function') {
            _setOffsetMarkers(prev => {
                const result = newMarkers(prev);
                offsetMarkersRef.current = result;
                return result;
            });
        } else {
            offsetMarkersRef.current = newMarkers;
            _setOffsetMarkers(newMarkers);
        }
    };
    const setDrawLine = (newLines: google.maps.Polyline[] | ((prev: google.maps.Polyline[]) => google.maps.Polyline[])) => {
        if (typeof newLines === 'function') {
            _setDrawLine(prev => {
                const result = newLines(prev);
                drawLineRef.current = result;
                return result;
            });
        } else {
            drawLineRef.current = newLines;
            _setDrawLine(newLines);
        }
    };
    const oneWaySearchDone = sessionStorage.getItem('oneWaySearchInit') ?? undefined;
    Modal.setAppElement('body');

    const fetchMapData = () => {
        try {
            const mapData= sessionStorage.getItem('allCars');
            if (mapData) {
                setMapMarkerData(JSON.parse(mapData));
            } else {
                setMapMarkerData([]);
                clearMarkers();
            }
        } catch (e) {
            if (dataContext.appContext !== "production") console.log(e);
        }
    }

    const fetchOneWayEnabled = () => {
        try {
            const oneWayRes = sessionStorage.getItem('oneway');
            oneWayRes && setOneWayEnabled(JSON.parse(oneWayRes));
        } catch (e) {
            if (dataContext.appContext !== "production") console.log(e);
        }
    }

    const resetOriginToVehicleLine = () => {
        originToVehicleLine?.setMap(null);
        setOriginToVehicleLine(null);
        setSelectedMarker(undefined);
    }

    useEffect(() => {
        if(typeof dataContext.oneWayRes === 'undefined' || dataContext.oneWayRes === null){
            fetchOneWayEnabled();
        } else {
            setOneWayEnabled(dataContext.oneWayRes);
        }
    }, [dataContext.oneWayRes]);

    useEffect(() => {
        // switch between map and default mode images
        if (!dataContext.mapCanInit) {
            setShowGoogleMap(false);
            return;
        }

        if ((dataContext.showDefaultResults && !dataContext.oneWayRes) ||
            (dataContext.oneWayRes && !oneWaySearchDone)) {
            setShowGoogleMap(false);
        } else { // allow for map visibility
            setShowGoogleMap(true);
        }
    }, [dataContext.oneWayRes, dataContext.showDefaultResults, dataContext.mapCanInit, oneWaySearchDone ]);

    useEffect(() => {
        if(showGoogleMap && dataContext.mapCanInit){
            if(dataContext.mapData && dataContext.mapData.length > 0){
                setMapMarkerData(dataContext.mapData);
            }else {
                fetchMapData();
            }
        }
    }, [dataContext.mapData, dataContext.mapCanInit, showGoogleMap, dataContext.showDefaultResults, oneWayEnabled, dataContext.searchParameters]);

    const currentTime = moment();
    const isNightTime = currentTime.hour() >= 21 || currentTime.hour() < 7;

    const selectVipVehicle = (vehicle: IVehicleSearch) => {
        setSelectedVipCar(vehicle);
        if (vehicle.privateData?.private) {
            setVipCodeCheckOpen(true);
        }
    }

    const getIconType = (data: IVehicleSearch): string => {
        let iconType: string = 'default';
        let icon: string = data.vehicleModelData.modelacriss;
        let van: boolean = data.vehicleModelData.type!.includes('van');
        let cloudTag: string[] = data.tagCloud;
        if(data.privateData?.vip){
            iconType = 'vip'
        } else {
            if (dataContext.acrissCodeSet?.compact.includes(icon) && !van) {
                iconType = 'compact';
            } else if (dataContext.acrissCodeSet?.stw.includes(icon)) {
                iconType = 'stw';
            } else if (dataContext.acrissCodeSet?.suv.includes(icon)) {
                iconType = 'suv';
            } else if (dataContext.acrissCodeSet?.minibus.includes(icon) || cloudTag.includes('minibussi')) {
                iconType = 'minibus';
            } else if (van) {
                iconType = 'van';
            }
        }

        return iconMapping[iconType];
    };

    const getIconUrl = (data: IVehicleSearch, multi?: boolean,
                        returnLocIcon?: boolean, multiNotAvailable?: boolean | undefined, multiGrayIcon?: boolean): string => {
        if (!data) {
            return '';
        }

        let iconTxt = getIconType(data);

        if (!data.availabilityData?.available) {
            iconTxt += '-unavailable';
        } else if (!multi) {
            let isVehicleInFilteredSet = false;
            dataContext.filteredVehicles?.forEach((filteredVehicle) => {
                if (isVehicleInFilteredSet) return;
                if(filteredVehicle.id === data.id) {
                    isVehicleInFilteredSet = true;
                }
            })

            if(!isVehicleInFilteredSet){
                // if car/van not included in filter setting results and available -->
                // show gray icon to make easier to see
                if(iconTxt.includes('car')) {
                    iconTxt = "car-grey"
                } else if (iconTxt.includes('van')){
                    iconTxt = "van-grey";
                }
            }
        }

        if (returnLocIcon) {
            // gray icons also used to show return locations for one-way vehicles
            if(iconTxt.includes('car')) {
                iconTxt = "car-grey"
            } else {
                iconTxt = "van-grey";
            }
        } else if (multi) {
            if (multiNotAvailable) {
                iconTxt = 'multiple-2-unavailable';
            } else {
                iconTxt = multiGrayIcon ? 'multiple-gray' : 'multiple-2';
            }
        }

        return `${dataContext.rootUrl}/assets/map-pin-vehicle-icons/map-pin-icon-${iconTxt}.png`;
    };

    const waitForGoogleMapsApi = (signal: AbortSignal): Promise<void> => {
        if (typeof google !== 'undefined' && google.maps && typeof google.maps.importLibrary === 'function') {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds
            const interval = setInterval(() => {
                if (signal.aborted) {
                    clearInterval(interval);
                    return;
                }
                attempts++;
                if (typeof google !== 'undefined' && google.maps && typeof google.maps.importLibrary === 'function') {
                    clearInterval(interval);
                    resolve();
                } else if (attempts >= maxAttempts) {
                    clearInterval(interval);
                    reject(new Error('Google Maps API failed to load'));
                }
            }, 100);
        });
    };

    const initialize = async (signal: AbortSignal) => {
        if(googleMap){
            return;
        }

        await waitForGoogleMapsApi(signal);
        if (signal.aborted) return;

        if (mapRef.current) {
            const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
            const constantDefaultMapZoomLevel: number = 14;
            const constantMinMapZoomLevel: number = 12;
            const constantMaxMapZoomLevel: number = 20;

            setGoogleMap(new Map(mapRef.current, {
                center: { lat, lng },
                zoom: constantDefaultMapZoomLevel,
                minZoom: constantMinMapZoomLevel,
                maxZoom: constantMaxMapZoomLevel,
                // Map styling (POI visibility, colors, etc.) is controlled by the cloud-based style for this mapId in Google Cloud Console — inline `styles` are ignored when mapId is set.
                mapId: mapId,
                mapTypeId: google.maps.MapTypeId.ROADMAP,
                mapTypeControl: false,
                fullscreenControl: false,
                streetViewControl: false,
                gestureHandling: 'greedy',
                zoomControl: true,
                zoomControlOptions: {
                    position: google.maps.ControlPosition.RIGHT_BOTTOM,
                },
                cameraControl: false,
                clickableIcons: false,
            }));
        }
    };

    useEffect(() => {
        if (dataContext.showDefaultResults || !dataContext.mapCanInit || !showGoogleMap ) return;
        sessionStorage.removeItem("carIdReservedCheck"); // remove this value if it exists from older session

        const controller = new AbortController();
        initialize(controller.signal);
        return () => { controller.abort(); };
    }, [dataContext.showDefaultResults, dataContext.mapCanInit, googleMap, showGoogleMap]);

    useEffect(() => {
        if(googleMap){
            googleMap.setCenter({ lat, lng });
            addGreenMarker({ lat, lng });
        }

    }, [lat, lng, dataContext.searchParameters, googleMap]);

    useEffect(() => {
        // if line available to be shown, this useEffect appends 1 line to map
        originToVehicleLine?.setMap(googleMap);
    }, [googleMap, originToVehicleLine])


    useEffect(() => {
        if(googleMap && greenMarker && greenMarker.position?.lat && greenMarker.position?.lng) {
            originToVehicleLine?.setMap(null)
            if ((selectedMarker || (dataContext.vehicle && dataContext.page !== 0)) && !dataContext.oneWayRes) {
                let vehicleLat = selectedMarker?.lat ?? dataContext.vehicle?.homeLocationData.coordinates.latitude;
                let vehicleLng = selectedMarker?.lng ?? dataContext.vehicle?.homeLocationData.coordinates.longitude;
                const originToVehiclePathCoordinates = [{ lat: greenMarker.position.lat, lng: greenMarker.position.lng },
                    { lat: vehicleLat, lng: vehicleLng }];
                const originToVehiclePath = new google.maps.Polyline({
                    path: originToVehiclePathCoordinates,
                    geodesic: true,
                    strokeColor: "#0bb395",
                    strokeOpacity: 1.0,
                    strokeWeight: 3,
                });

                setOriginToVehicleLine(originToVehiclePath);
            } else {
                if (originToVehicleLine) { // only run function if a polyline exists
                    resetOriginToVehicleLine();
                }
            }
        }

    }, [greenMarker, googleMap, selectedMarker, dataContext.vehicle, dataContext.page, dataContext.oneWayRes, dataContext.noVatPrices])

    const clearMarkers = () => {
        InfoWindowSingleton.getInstance().close();
        resetOriginToVehicleLine();

        // Use refs for imperative Google Maps cleanup to avoid stale closure issues
        if(markersRef.current && markersRef.current.length > 0){
            markersRef.current.forEach((marker: IMarker) => marker.marker.map = null);
            setMarkers([]);
        }

        if(offsetMarkersRef.current && offsetMarkersRef.current.length > 0){
            offsetMarkersRef.current.forEach((marker: IMarker) => marker.marker.map = null);
            setOffsetMarkers([]);
        }

        if(drawLineRef.current && drawLineRef.current.length > 0){
            drawLineRef.current.forEach((line: google.maps.Polyline) => line.setMap(null));
            setDrawLine([]);
        }

        if(onewayReturnMarker){
            // clear one-way return marker if one exists
            onewayReturnMarker.remove();
            onewayReturnMarker.map = null;
            setOnewayReturnMarker(null);
        }

        setMarkersLoading(true);
    }

    const handleMarkerPopulate = (mapData: IVehicleSearch[]) => {
        if(!showGoogleMap || markersLoading) return;

        if(mapData && mapData.length > 0) {
            if(markersRef.current.length >= 1) { // If there are markers on the map
                clearMarkers();
            }
            setMarkersLoading(true);
        } else if (markersRef.current.length >= 1) {
            // Data is empty — clear existing markers (e.g. search params changed)
            clearMarkers();
            setMarkersLoading(false);
        }
    }

    useEffect(() => {
        handleMarkerPopulate(mapMarkerData);
    }, [mapMarkerData, oneWayEnabled, showGoogleMap, dataContext.showDefaultResults, dataContext.vehicle, dataContext.filteredVehicles, dataContext.noVatPrices]);

    const handleDatePicker = (id?: number) => {
        if (id) {
            reservationArr(id);
            // save id to use if email alert activation needed from map modal
            sessionStorage.setItem("carIdReservedCheck", id.toString());
        } else {
            sessionStorage.removeItem("carIdReservedCheck");
        }
        setReservationTable(true);
        if (reservationTable) {
            setReservationTable(false);
        }
    }

    const selectVehicle = (vehicle: IVehicleSearch)=> {
        if (vehicle.availabilityData?.available) {
            setSelectedMarker(undefined);
            if (vehicle.id !== dataContext.vehicle?.id) {
                sessionStorage.removeItem("customer");
                sessionStorage.removeItem("services");
            }
            if (dataContext.customer && dataContext.vehicle) {
                if (dataContext.customer.dateOfBirth !== '' || dataContext.customer.personalIdCode !== '') {
                    if (isUnderTwentyOne(dataContext.customer.dateOfBirth, dataContext.customer.personalIdCode) && vehicle) {
                        if (vehicle.vehicleData.noundertwentyone) {
                            dataContext.changeTwentyModal(true);
                            return;
                        }
                    }
                }
            }
            dataContext.changeVehicle(vehicle);
            dataContext.changePage(1)
            dataContext.changeMapData([vehicle]); // Set mapData to only include the selected vehicle
            dataContext.changeSearchParameters(dataContext.searchParameters);

            if (dataContext.user) {
                sessionStorage.setItem("user", JSON.stringify(dataContext.user));
            }
            dataContext.changeRoute("/makereservation");
            if (!dataContext.oneWayRes) {
                dataContext.sendGa4Event("select_item", {
                    item_list_id: "map_list",
                    item_list_name: "Map Results",
                    selection_surface: "map",
                    items: [buildVehicleItem(vehicle, { noVat: dataContext.noVatPrices })]
                });                
            } else {
                dataContext.sendGa4Event("select_item", {
                    item_list_id: "oneway_map_list",
                    item_list_name: "OneWay Map Results",
                    selection_surface: "map",
                    items: [buildVehicleItem(vehicle, { noVat: dataContext.noVatPrices })]
                });                
            }
        } else {
            handleDatePicker(vehicle.id);
            if (!dataContext.oneWayRes) {
                dataContext.sendGa4Event("open_availability_calendar", {
                    flow: "a2a",
                    surface: "map",
                    item_list_id: "map_list",
                    item_list_name: "Map Results",
                    vehicle_id: vehicle.id
                });                
            } else {
                dataContext.sendGa4Event("open_availability_calendar", {
                    flow: "oneway",
                    surface: "map",
                    item_list_id: "oneway_map_list",
                    item_list_name: "OneWay Map Results",
                    vehicle_id: vehicle.id
                });                
            }
        }
    }

    const reservationArr = async (vehicleId: number) => {
        const start = moment(dataContext.searchParameters?.startDate).format('YYYY-MM-DD')
        const end = moment(dataContext.searchParameters?.endDate).format('YYYY-MM-DD')

        const params = {
            startDate: start,
            startTime: dataContext.searchParameters?.startTime,
            endDate: end,
            endTime: dataContext.searchParameters?.endTime,
            oneWay: dataContext.oneWayRes ? 1 : undefined,
            pickupCity: dataContext.oneWayRes ? dataContext.searchParameters?.pickupCity : undefined
        }

        try {
            if(vehicleId && params) {
                const response = await axios.get(`${dataContext.v3ApiUrl}/anonymous/vehicles/${vehicleId}/reserved-intervals`, {params})
                if (response.data.data.reservedIntervals) {
                    setReservedSlots(response.data.data.reservedIntervals);
                }
                if (response.data.data.messageFi && response.data.data.messageEn) {
                    setReservedSlotsMessages(dataContext.language === "en" ? response.data.data.messageEn : response.data.data.messageFi);
                }
            }
        } catch (e) {
            if (dataContext.appContext !== "production") console.log(e);
        }
    }

    useEffect(() => {
        // show one-way vehicle's return location marker on map
        if(googleMap && dataContext.showOneWayReturnLocation && oneWayEnabled) {
            let oneWayVehicleData;
            mapMarkerData.forEach((vehicleMarker: IVehicleSearch) => {
                if (vehicleMarker.id === dataContext.showOneWayReturnLocation) {
                    oneWayVehicleData = vehicleMarker;
                }
            })

            if (oneWayVehicleData) {
                addOnewayReturnMarker(oneWayVehicleData).finally(dataContext.changeShowOneWayReturnLocation(undefined));
            } else {
                dataContext.changeShowOneWayReturnLocation(undefined);
            }
        }
    }, [dataContext.showOneWayReturnLocation, googleMap, oneWayEnabled])

    const groupVehiclesByLocation = (vehicles: IVehicleSearch[]): IVehicleSearch[][] => {
        const locationMap = new Map<string, IVehicleSearch[]>();

        vehicles.forEach((vehicle: IVehicleSearch) => {
            const key: string = `${vehicle.homeLocationData.coordinates.latitude.toFixed(12)},${vehicle.homeLocationData.coordinates.longitude.toFixed(12)}`;
            const group: IVehicleSearch[] | undefined = locationMap.get(key);

            if (group) {
                group.push({...vehicle, inCluster: true});
            } else {
                locationMap.set(key, [{...vehicle, inCluster: false}]);
            }
        });

        return Array.from(locationMap.values());
    };

    const addGreenMarker = async (position: google.maps.LatLngLiteral) => { // Remove old green marker from the map
        if(greenMarker){
            greenMarker.remove();
            greenMarker.map = null;
            setGreenMarker(null);
        }

        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
        const icon: HTMLImageElement = document.createElement('img');
        icon.src = `${dataContext.rootUrl}/assets/icons/new/map-icon-locate.png`;
        const marker: google.maps.marker.AdvancedMarkerElement = new AdvancedMarkerElement({
            position,
            map: googleMap,
            content: icon,
            zIndex: 100,
        });
        setGreenMarker(marker);
        return marker;
    }

    const addOnewayReturnMarker = async (vehicle: IVehicleSearch) => { // Remove old green marker from the map
        if(onewayReturnMarker){
            onewayReturnMarker.remove();
            onewayReturnMarker.map = null;
            setOnewayReturnMarker(null);
        }

        if (!vehicle.returnLocationData?.coordinates.latitude || !vehicle.returnLocationData?.coordinates.longitude || !googleMap || !oneWayEnabled) return;

        InfoWindowSingleton.getInstance().close(); // close any other info windows that are open

        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        const icon: HTMLImageElement = document.createElement('img');
        icon.src = getIconUrl(vehicle, false, true);

        // one-way zIndex only considers individual markers availability (clusters are not common for one way map results)
        let zIndexValue = vehicle.availabilityData?.available ? 70 : 20;

        const position: google.maps.LatLngLiteral = {lat: Number(vehicle.returnLocationData.coordinates.latitude.toFixed(12)),
            lng: Number(vehicle.returnLocationData.coordinates.longitude.toFixed(12))}
        const marker: google.maps.marker.AdvancedMarkerElement = new AdvancedMarkerElement({
            position,
            map: googleMap,
            content: icon,
            gmpClickable: true,
            zIndex: zIndexValue,
        });

        marker.addListener('gmp-click', () => {
            const contentString: string = getInfoWindowContent(vehicle, true);
            InfoWindowSingleton.getInstance().setContent(contentString);
            // @ts-ignore
            InfoWindowSingleton.getInstance().open(googleMap, marker);
        })

        setOnewayReturnMarker(marker);
        googleMap?.panTo(position);
    }

    const getInfoWindowContent = (vehicle: IVehicleSearch, returnLocation?: boolean) => {
        const price: number = vehicle.onsale ? baseOnSalesPrice(vehicle, dataContext.noVatPrices) : basePrice(vehicle, dataContext.noVatPrices);
        const isVipCodeValid = vehicle.privateData?.vip && !vehicle?.privateData?.private;
        const couponValid = ((dataContext.storedCoupon?.valid &&
                (dataContext.noVatPrices ? vehicle?.pricingDataNoVat.coupon !== null : vehicle?.pricingData.coupon !== null))
            && !vehicle?.onsale && vehicle?.extraOnsales?.extra !== 1 && !vehicle?.privateData?.private);
        const discount = couponValid ? calculateDiscountValue(vehicle!, dataContext.noVatPrices) : vehicle?.onsale ? ((1.0-vehicle?.onsale.discountmultiplier)*vehicle.onsale.price)/100 : 0;

        return `<div style="text-align: center; font-family: 'Nunito', Helvetica, Arial, Lucida, sans-serif;" class="rrm-font">
            <img  width="150" height="100" src="https://vehicles-cdn.24rent.fi/${vehicle.id}_medium.jpeg" style="width: 150px; height: 100px; object-fit: cover; border-radius: 10%;" loading="lazy"/>
            <h2 style="text-align: center; font-size: 18px !important; padding-top: 5px; margin-bottom: 4px; font-family: ${fontFamily};">${vehicle.vehicleModelData.manufacturer} ${vehicle.vehicleModelData.name} ${vehicle.vehicleModelData.acriss ?? ''} ${vehicle.vehicleModelData.dimensions.trunkVolume !== 0 ? 
            vehicle.vehicleModelData.dimensions.trunkVolume + 'm³' : ''} </h2>  
            ${vehicle.availabilityData!.available ? `<p class="adress" style="font-weight: lighter; font-size: 0.875rem; text-align: center; margin-bottom: 4px;">${vehicle.homeLocationData.address}</p>` : ''}
            ${vehicle.availabilityData!.available ? `<div class="price" style="background-color: #0BB395; border-radius: 5px; padding: 5px; ">${discount > 0 ?
            `<p style="text-align: center; color: white; text-decoration: line-through; margin-bottom: unset; ">${priceFormat(price)} €</p> <p style="text-align: center; color: white; font-weight: bold; margin-bottom: unset; font-family: 'Nunito', Helvetica, Arial, Lucida, sans-serif;"> 
            ${priceFormat(price - discount)} € </p>` : `<p style="text-align: center; color: white; font-weight: bold; margin-bottom: unset;">${priceFormat(price)} €</p>`} </div>` : ''}
            ${!vehicle.availabilityData!.available ? `<label style="color: red; font-weight: bold; margin: unset; display: flex; justify-content: center; "> ${translateService.translate('vehicleSearch1.Reserved')}</label>` : ''}
           ${!returnLocation ?  `<button id="selectCar-${vehicle.id}" style="background-color: #FBB022; border-radius: 30px; padding: 14px 20px; color: white; font-weight: bold; width: 100%; margin-top: 5px; border: none;" >
    ${vehicle.availabilityData!.available ? (!vehicle.privateData?.private || isVipCodeValid) ?
                    translateService.translate('vehicleSearch.selectCar')
                    : translateService.translate('vehicleSearch1.privateSelection')
                : translateService.translate('vehicleSearch1.reservedSearch')}
    </button>` :
            `<button style="background-color: #FBB022; border-radius: 30px; padding: 14px 20px; color: white; font-weight: bold; width: 100%; margin-top: 5px; border: none;>
    ${translateService.translate('vehicleSearch1.returnLocation')}
    </button>`}
     </div>`;
    }

    useEffect(() => {
        if (dataContext.openPopupInCluster && offsetMarkers && offsetMarkers.length > 0) {
            let markerInCluster = offsetMarkers.find((offsetMarker) => offsetMarker.vehicleId === dataContext.openPopupInCluster);
            if(markerInCluster){
                InfoWindowSingleton.getInstance().close();
                markerInCluster.marker.click();
                // add line set here as well
                dataContext.changeOpenPopupInCluster(undefined);
            }
        }
    }, [dataContext.openPopupInCluster, offsetMarkers])

    const createOffSetMarker = (position: google.maps.LatLngLiteral, vehicle: IVehicleSearch, index: number, total: number) => {
        const radius: number = 0.0001;
        const divisor: number = total === 2 ? 2 : total; // If there are only two vehicles, divide the circle into three parts
        const angleStep: number = (2 * Math.PI) / divisor; // Angle between each vehicle
        const angle: number = angleStep * index;

        // Calculate the position of the offset markers
        const offsetPosition: { lat: number, lng: number } = {
            lat: position.lat + radius * Math.cos(angle),
            lng: position.lng + radius * Math.sin(angle)
        };

        const offsetIconUrl: string = getIconUrl(vehicle);
        const offsetIcon: HTMLImageElement = document.createElement('img');
        offsetIcon.id = `${vehicle.id}`;
        offsetIcon.src = offsetIconUrl;

        // Create the offset marker
        const offsetMarker: google.maps.marker.AdvancedMarkerElement = new google.maps.marker.AdvancedMarkerElement({
            position: offsetPosition,
            map: googleMap,
            content: offsetIcon,
            gmpClickable: true,
            gmpDraggable: false,
            zIndex: vehicle.availabilityData?.available ? 50 : 20, // individual vehicle: only availability checked for index priority
        });

        // Create a line from the central point to the offset marker
        const line = new google.maps.Polyline({
            path: [position, offsetPosition],
            geodesic: true,
            strokeColor: '#000000',
            strokeOpacity: 1.0,
            strokeWeight: 2,
            map: googleMap,
        });
        // Add the line to the drawLine state
        setDrawLine(prevLines => [...prevLines, line]);
        // Add the offset marker to the offsetMarkers state
        setOffsetMarkers(prevMarkers => [...prevMarkers, { vehicleId: vehicle.id, marker: offsetMarker, inCluster: true }]);

        InfoWindowSingleton.clearListeners(); // Clear existing event listeners

        offsetMarker.addListener('gmp-click', () => {
            const contentString: string = getInfoWindowContent(vehicle);
            google.maps.event.addListener(InfoWindowSingleton.getInstance(), 'domready', () => {
                const selectCarButton = document.getElementById('selectCar-' + vehicle.id);
                if (selectCarButton) {
                    selectCarButton.addEventListener('click', () => {
                       if (!dataContext.vehicle) {
                           if(vehicle.privateData?.vip && vehicle?.privateData?.private){
                               selectVipVehicle(vehicle);
                           } else {
                               selectVehicle(vehicle);
                           }
                       }
                    });
                }
            });

            InfoWindowSingleton.getInstance().close(); // Close the current infoWindow if it exists

            if(!dataContext.oneWayRes && vehicle.homeLocationData.coordinates.latitude && vehicle.homeLocationData.coordinates.longitude) {
                // set lat long for connection line to be drawn
                setSelectedMarker({lat: vehicle.homeLocationData.coordinates.latitude, lng: vehicle.homeLocationData.coordinates.longitude})
            } else { setSelectedMarker(undefined) }

            InfoWindowSingleton.getInstance().setContent(contentString);
            // @ts-ignore
            InfoWindowSingleton.getInstance().open(googleMap, offsetMarker);

        })
    }

    const handleMarkerClick = ({marker, vehicle}: handleMarkerClickProps ) => {
        const contentString: string = getInfoWindowContent(vehicle);

        // Clear existing event listeners
        InfoWindowSingleton.clearListeners();

        google.maps.event.addListener(InfoWindowSingleton.getInstance(), 'domready', () => {
            const selectCarButton = document.getElementById('selectCar-' + vehicle.id);
            if (selectCarButton) {
                selectCarButton.addEventListener('click', () => {
                    if (!dataContext.vehicle) { // do not allow vehicle selection if vehicle already selected
                        if(vehicle.privateData?.vip && vehicle?.privateData?.private){
                            selectVipVehicle(vehicle);
                        } else {
                            selectVehicle(vehicle);
                        }
                    }
                });
            }
        });

        InfoWindowSingleton.getInstance().close(); // Close the current infoWindow if it exists

        if(!dataContext.oneWayRes && vehicle.homeLocationData.coordinates.latitude && vehicle.homeLocationData.coordinates.longitude) {
            // set lat long for connection line to be drawn
            setSelectedMarker({lat: vehicle.homeLocationData.coordinates.latitude, lng: vehicle.homeLocationData.coordinates.longitude})
        } else { setSelectedMarker(undefined) }

        InfoWindowSingleton.getInstance().setContent(contentString);
        // @ts-ignore
        InfoWindowSingleton.getInstance().open(googleMap, marker);


    };

    const createMarker = async (vehicle: IVehicleSearch, group: IVehicleSearch[], total: number): Promise<IMarker> => {
        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        const position = { lat: vehicle.homeLocationData.coordinates.latitude,
            lng: vehicle.homeLocationData.coordinates.longitude }

        let multiNotAvailable = undefined;
        let multiGrayIcon = false;
        if (total > 1 && group) {
            // determine if cluster contains all unavailable vehicles for icon mapping
            let unavailableCount = 0;
            group.forEach((innerVehicle) => {
                if (innerVehicle.availabilityData?.available === false) {
                    unavailableCount++;
                }
            });
            if (unavailableCount === total) {
                multiNotAvailable = true;
            }

            if (!multiNotAvailable) {
                // check if all inner vehicles are included in filtered set
                let isAnyVehicleFiltered = group.some((innerVehicle) =>
                    dataContext.filteredVehicles?.some((filteredVehicle) =>
                        filteredVehicle.id === innerVehicle.id)
                );
                if (!isAnyVehicleFiltered) {
                    // if no vehicles are included in filter set, show gray icon
                    multiGrayIcon = true;
                }
            }
        }

        let iconUrl: string = getIconUrl(vehicle, total > 1, undefined, multiNotAvailable, multiGrayIcon); // If there are multiple vehicles, use the multiple icon
        let icon: HTMLImageElement = document.createElement('img');
        icon.src = iconUrl;
        icon.id = `${vehicle.id}`;

        let zIndexValue = 10;
        if (!(total > 1 && group)) {
            if (vehicle.availabilityData?.available) {
                zIndexValue = 70;
            } else {
                zIndexValue = 20;
            }
        } else if (!multiNotAvailable) {
            zIndexValue = 50;
        }

        // this is either the three dot marker or a single vehicle marker
        const marker: google.maps.marker.AdvancedMarkerElement = new AdvancedMarkerElement({
            position,
            map: googleMap,
            content: icon,
            gmpClickable: true,
            gmpDraggable: false,
            zIndex: zIndexValue,
        });

        if (googleMap) {
            marker.addListener('gmp-click', () => {
                if(total > 1) {
                    if (offsetMarkersRef.current.length >= 1) {
                        offsetMarkersRef.current.forEach((marker: IMarker) => marker.marker.map = null);
                        setOffsetMarkers([]);
                    }
                    // Zoom map to the location of the group of vehicles
                    googleMap.setCenter(position);

                    // Define the final zoom level and the zoom increment
                    const finalZoom = 19;
                    const zoomIncrement = 0.5;

                    // Start the zoom animation
                    let currentZoom = googleMap.getZoom() ?? 1;
                    const zoomInterval = setInterval(() => {
                        if (currentZoom >= finalZoom) {
                            clearInterval(zoomInterval);
                        } else {
                            currentZoom += zoomIncrement;
                            googleMap.setZoom(currentZoom);
                        }
                    }, 100); // Adjust the interval time to control the speed of the zoom animation


                    group.forEach((vehicle: IVehicleSearch, groupIndex: number) => {
                        createOffSetMarker(position, vehicle, groupIndex, total);
                    });

                    // Remove the click event listener to make the cluster marker un-clickable
                    // As offset markers have been created with this marker at center, this avoids
                    // over clicking which creates dark borders around offset markers
                    google.maps.event.clearListeners(marker, 'gmp-click');
                } else {
                    googleMap.panTo(position);
                    handleMarkerClick({marker, vehicle});
                }

                // Close the infoWindow when the map is clicked
                googleMap.addListener('click', () => {
                    setSelectedMarker(undefined);
                    InfoWindowSingleton.getInstance().close();
                });
            });
        }
        return {
            vehicleId: vehicle.id,
            inCluster: (total > 1),
            marker
        }
    };

    const createPromiseMarkers = (vehicleGroups: IVehicleSearch[][]) => {
        const promiseMarkers: Promise<IMarker>[] = [];
        vehicleGroups.forEach((group: IVehicleSearch[]) => {
            group.forEach((vehicle: IVehicleSearch) => {
                promiseMarkers.push(createMarker(vehicle, group, group.length));
            });
        });

        return promiseMarkers;
    }

    const createMarkersAndSetOnMap = async (mapData: IVehicleSearch[]) => {
        if (!mapData){
            throw new Error("No map data provided");
        }

        const vehicleGroups: IVehicleSearch[][] = groupVehiclesByLocation(mapData);
        const promiseMarkers: Promise<IMarker>[] = createPromiseMarkers(vehicleGroups);

        try {
            const newMarkers: IMarker[] = await Promise.all(promiseMarkers);
            const mappedMarkers: IMarker[] = newMarkers.map((marker: IMarker) =>
                ({ vehicleId: marker.vehicleId, marker: marker.marker, inCluster: marker.inCluster}));
            setMarkers(mappedMarkers);
            dataContext.changeMapMarkers(newMarkers);
        } catch (error) {
            console.error("Error creating markers: ", error);
        } finally {
            googleMap!.setZoom(10);
        }
    }

    useEffect(() => {
        if (!googleMap) { // If Google Map is not loaded, return
            return;
        }

        // prevent marker creation if there are markers already on the map (use ref for latest value)
        if(markersRef.current.length > 0){
            return;
        }

        if (markersLoading) { // If markers are loading, create them
            let createPromise: Promise<void> | undefined;
            if(mapMarkerData && mapMarkerData.length > 0 && !dataContext.vehicle){
                createPromise = createMarkersAndSetOnMap(mapMarkerData);
            } else if(dataContext.vehicle){
                createPromise = createMarkersAndSetOnMap([dataContext.vehicle]);
            }

            // Only clear loading after async marker creation completes
            if (createPromise) {
                createPromise.finally(() => setMarkersLoading(false));
            } else {
                setMarkersLoading(false);
            }
        }
    }, [markersLoading, oneWayEnabled, googleMap]);

    return (<>
        <div className="stickyDiv">
            <Modal isOpen={reservationTable} style={cStylesMaps}>
                <div>
                    <div className={'row resTblContainer'}>
                        <div className={'col-6'}></div>
                        <div className={'col-6 d-flex justify-content-end mb-3'}>
                            <button className='btn btn-sm btn-primary btn-all'
                                    onClick={() => {handleDatePicker()}}> X </button>
                        </div>
                    </div>
                    <CalendarComponent isVisible={reservationTable} reservedIntervals={reservedSlots}
                                       vehicleId={sessionStorage.getItem('carIdReservedCheck') ?? '-1' }
                                       reservedMessages={reservedSlotsMessages} />
                </div>
            </Modal>
            <div className="position-relative">
                <Backdrop
                    sx={{ color: '#fbb040', zIndex: (theme) => theme.zIndex.drawer + 1005, position: "absolute", backdropFilter: isNightTime ? "blur(5px)" : "none" }}
                    open={((!dataContext.oneWayRes && dataContext.showDefaultResults) || (isNightTime && !dataContext.customer?.loggedIn)) ?? false}>
                    {isNightTime && !dataContext.showDefaultResults ?
                        <button id="mapLoginBtn"
                                className={'btn btn-primary btn-outline-primary btn-all font-weight-bold loginFirst'}
                                onClick={() => {
                                    document.getElementById("searchbox-label")?.scrollIntoView({
                                        behavior: 'smooth', block: 'center'});
                                }}>
                            {translateService.translate('vehicleSearch1.afterHourLogin')} </button> :
                        <button className={'btn btn-primary btn-outline-primary btn-all font-weight-bold loginFirst'}
                                onClick={() => {
                                    document.getElementById("searchbox-label")?.scrollIntoView({
                                        behavior: 'smooth', block: 'center'});
                                    (document.querySelector('.searchbox-input') as HTMLElement)?.focus();
                                }}>
                            {translateService.translate('vehicleSearch1.selectLocationAndDate')} </button>
                    }
                </Backdrop>
                {selectedVipCar ? <VipCodeFromMap vehicle={selectedVipCar} selectVehicle={selectVehicle}
                                                  vipCodeCheckOpen={vipCodeCheckOpen}
                                                  setVipCodeCheckOpen={setVipCodeCheckOpen}
                                                  clearSelectedVehicle={() => setSelectedVipCar(undefined)}/> : null}
                <div className={oneWayEnabled ? "default-oneway-map-image" : "default-map-image"} hidden={showGoogleMap}></div>
                { dataContext.mapCanInit &&
                    <div ref={mapRef} hidden={!showGoogleMap} style={{ width: width ?? "100%", height: "100%" }} />}

            </div>
        </div>
    </>);
}

export default GoogleMap;